import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { dialogs } from "../../context/DialogContext";
import api from "../../services/api";
import { cargarPropiedadExpediente } from "../../utils/resolverPropiedadCompraCliente";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import { calleLineaDesdePlaces } from "../../utils/googlePlacesAddressLine";

function BuscadorPropiedadInline({ onSeleccionar, isDark }) {
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const timerRef = useRef(null);

  const buscar = (q) => {
    setTexto(q);
    clearTimeout(timerRef.current);
    if (q.length < 1) { setResultados([]); setAbierto(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/propiedades?buscar=${encodeURIComponent(q)}&perPage=8&cartera=todas`);
        setResultados(r.data.data || r.data.propiedades || []);
        setAbierto(true);
      } catch { setResultados([]); }
    }, 300);
  };

  const inputSt = {
    padding: "6px 8px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#1f2937" : "#fff",
    border: `1px solid ${isDark ? "#374151" : "#c8c8be"}`,
    color: isDark ? "#f9fafb" : "#111827", outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "relative" }}>
      <input value={texto} onChange={(e) => buscar(e.target.value)} placeholder="Ref. o dirección..." style={inputSt}
        onBlur={() => setTimeout(() => setAbierto(false), 150)} />
      {abierto && resultados.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 400,
          background: isDark ? "#1e2330" : "#fff", border: `1px solid ${isDark ? "#374151" : "#d1d5db"}`,
          borderRadius: 6, boxShadow: "0 6px 18px rgba(0,0,0,0.12)", maxHeight: 160, overflowY: "auto", marginTop: 2,
        }}>
          {resultados.map((p) => (
            <div key={p.id} onMouseDown={() => { onSeleccionar(p); setTexto(""); setAbierto(false); }}
              style={{ padding: "6px 8px", cursor: "pointer", fontSize: 12, borderBottom: `1px solid ${isDark ? "#374151" : "#f3f4f6"}`, color: isDark ? "#fff" : "#111" }}>
              <strong>{p.referencia}</strong> — {p.direccion || "Sin dirección"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ModalAnadirPropiedadExpediente({ isOpen, onClose, onConfirm, propiedadesActuales = [] }) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#ffffff" : t.textPrimary;

  const [form, setForm] = useState({
    propiedad_id: null, referencia: "", direccion: "", poblacion: "", provincia: "", vpo: false,
    _cargada: null,
  });

  if (!isOpen) return null;

  const inp = {
    padding: "6px 8px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#1a2030" : t.inputBg,
    border: `1px solid ${isDark ? "#5a6a80" : t.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };

  const seleccionarPropiedad = async (pr) => {
    if (propiedadesActuales.some((p) => p.propiedad_id === pr.id)) {
      dialogs.alert("Esta propiedad ya está en el expediente.");
      return;
    }
    const cargada = await cargarPropiedadExpediente({ propiedad_id: pr.id, referencia: pr.referencia });
    if (cargada?.propiedades?.[0]) {
      const p = cargada.propiedades[0];
      setForm({
        propiedad_id: p.propiedad_id,
        referencia: p.referencia || pr.referencia,
        direccion: p.direccion || pr.direccion || "",
        poblacion: p.poblacion || pr.poblacion || "",
        provincia: p.provincia || pr.provincia || "",
        vpo: !!p.vpo,
        _cargada: cargada,
      });
    } else {
      setForm({
        propiedad_id: pr.id,
        referencia: pr.referencia || "",
        direccion: pr.direccion || "",
        poblacion: pr.poblacion || "",
        provincia: pr.provincia || "",
        vpo: false,
        _cargada: null,
      });
    }
  };

  const confirmar = () => {
    if (!form.referencia?.trim() && !form.propiedad_id) {
      dialogs.alert("Indica una referencia o busca una propiedad.");
      return;
    }
    onConfirm({
      propiedad: {
        propiedad_id: form.propiedad_id,
        referencia: form.referencia.trim(),
        direccion: form.direccion.trim(),
        poblacion: form.poblacion.trim(),
        provincia: form.provincia.trim(),
        vpo: form.vpo,
      },
      extra: form._cargada,
    });
    setForm({ propiedad_id: null, referencia: "", direccion: "", poblacion: "", provincia: "", vpo: false, _cargada: null });
    onClose();
  };

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 12000, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: 440, maxWidth: "94vw", background: isDark ? "#1e2330" : "#fff",
        borderRadius: 10, padding: 16, border: `1px solid ${isDark ? "#4a5568" : t.border}`, color: tx,
      }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Añadir propiedad</div>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, opacity: 0.8 }}>Buscar propiedad</div>
        <BuscadorPropiedadInline isDark={isDark} onSeleccionar={seleccionarPropiedad} />
        <div style={{ display: "grid", gridTemplateColumns: "88px 1fr", gap: 8, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>Ref.</label>
            <input style={inp} value={form.referencia} onChange={(e) => setForm((f) => ({ ...f, referencia: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>Dirección</label>
            <AddressAutocompleteInput
              value={form.direccion}
              onValueChange={(v) => setForm((f) => ({ ...f, direccion: v }))}
              onPlaceAddressResolved={(parts) => setForm((f) => ({
                ...f,
                direccion: calleLineaDesdePlaces(parts) || f.direccion,
                poblacion: parts.poblacion || f.poblacion,
                provincia: parts.provincia || f.provincia,
              }))}
              style={inp}
            />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>Población</label>
            <input style={inp} value={form.poblacion} onChange={(e) => setForm((f) => ({ ...f, poblacion: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>Provincia</label>
            <input style={inp} value={form.provincia} onChange={(e) => setForm((f) => ({ ...f, provincia: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onClose}
            style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: tx, cursor: "pointer" }}>
            Cancelar
          </button>
          <button type="button" onClick={confirmar}
            style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#0891b2", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
            Añadir
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
